/**
 * Document TOC + Overview Ruler Prototype
 * Demonstrates:
 *   1. Sticky "current section" pill that triggers the TOC panel
 *   2. TOC sliding panel with active heading tracking
 *   3. Custom Overview Ruler replacing the native scrollbar
 *
 * Access: http://flownotes.localhost/?test=doc-toc-ruler
 */

import React, { useState, useRef, useEffect } from 'react'
import { List, X, Pin, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Heading {
  id: string
  level: 1 | 2 | 3
  text: string
  /** Proportional position in the document (0�1) � computed after render */
  ratio: number
}

// ---------------------------------------------------------------------------
// Mock long document content
// ---------------------------------------------------------------------------

const MOCK_CONTENT = `# exida Web Redevelopment � Project Plan

## Overview

This document captures the complete plan for the exida website redevelopment project. The scope includes a full redesign and rebuild of the product pages for SILstat, SIL Verification, and the SAEL Safety device page, along with a new FSE 107 course overview page.

The project is a Wix Studio build. All design work is completed or in active iteration. Development begins once designs are approved.

---

## Phase 1 � Design Approval

### SILstat Landing Page

The SILstat landing page redesign introduces a new hero layout with the card-grid product feature section. Designs are in Figma and have received initial client feedback.

**Open items:**
- Client to confirm icon style for the feature cards
- Confirm whether the "Request Demo" CTA links to a form or a calendar booking

**Status:** Ready for final review with Todd.

### SIL Verification Page

The SIL Verification page follows the same visual language as SILstat but with distinct content structure � three-column comparison table and an interactive diagram.

The interactive diagram is a scope risk. We have agreed to build a static version first and revisit interactivity if time allows.

**Open items:**
- Final copy for the three comparison columns
- Confirm diagram format: static SVG or animated

**Status:** Design in progress, ~80% complete.

### SAEL Safety Device Page

A new page for the SAEL Safety hardware product. The brief was received in March and design kicked off April 14.

This page has the most open questions of the three � the client is still finalizing product positioning copy.

**Open items:**
- Final product description paragraph from Todd
- Hero image / product photography (or 3D render)
- Confirm CTA: "Request Info" or "Contact Sales"

**Status:** Shaping. Design brief received, design work beginning.

---

## Phase 2 � Content & Asset Delivery

### Asset Inventory

All design assets are stored in Google Drive under:
Shared Drives / Hop Clients / Exida / Client Assets /
- SILstat Images & Icons/
- SIL Verification Images & Icons/
- SAEL Safety/
- FSE 107/

### Copy Requirements

Each page requires final approved copy before development can begin. Todd is the primary copy owner on the exida side.

**Priority order for copy delivery:**
1. SILstat � hero headline and subheading
2. SIL Verification � comparison table cell content
3. SAEL Safety � full page copy
4. FSE 107 � course description and outcomes list

### Photography

SILstat and SIL Verification use stock photography already selected and cleared. SAEL Safety requires new product photography or a 3D render of the hardware unit.

---

## Phase 3 � Development

### SILstat Build

**Estimated dev time:** 4-6 hours

The SILstat build is the most straightforward of the four pages. The component library from the existing exida Wix site will be reused where possible.

#### Hero Section
Full-width background image with headline overlay and a prominent CTA button.

#### Feature Card Grid
Six cards in a 3x2 grid. Each card has an icon, title, and 2-line body.

#### Social Proof Strip
Logo strip of 5-6 client logos. Static row, no carousel.

#### Bottom CTA Banner
Contrasting background, headline + subheading + single button.

### SIL Verification Build

**Estimated dev time:** 6-8 hours

Slightly more complex due to the comparison table and diagram section.

#### Comparison Table
Three-column table comparing Standard, Professional, and Enterprise tiers.

#### How It Works
Step-by-step section with numbered steps and icons. 4 steps total.

#### Static Diagram
SVG asset from Alice showing the SIL verification workflow.

### SAEL Safety Build

**Estimated dev time:** 4-5 hours

#### Product Hero
Hardware photograph with headline and spec highlights overlay.

#### Key Specs Section
Icon grid: 6 specs (voltage, temperature range, certifications, form factor, interface, warranty).

#### Use Case List
Three use cases with heading, body, and icon. 3-col on desktop, stacked on mobile.

#### Contact Form
Wix native form routing to info@exida.com.

### FSE 107 Course Overview Build

**Estimated dev time:** 3-4 hours

#### Course Header
Level badge ("Intermediate"), course code, title, and duration pill.

#### Learning Outcomes
Bulleted list of 6-8 outcomes from the course description document.

#### Course Structure Accordion
4 modules, each expandable. Wix native accordion component.

#### Enroll CTA
Button linking to the exida training registration portal.
## Phase 4 � QA & Launch

### QA Process

All pages go through a standard QA checklist:

- [ ] Desktop layout (1440px)
- [ ] Tablet layout (768px)
- [ ] Mobile layout (375px)
- [ ] All links functional
- [ ] Forms submit correctly
- [ ] Images load and have alt text
- [ ] Page speed within acceptable range
- [ ] Client review and sign-off

### Launch Sequence

Pages launch in this order to minimize disruption to existing SEO:

1. SILstat (replaces existing page)
2. SIL Verification (replaces existing page)
3. SAEL Safety (new page, lower risk)
4. FSE 107 (new page, lower risk)

---

## Timeline

### Planned Milestones

| Milestone | Target Date | Status |
|---|---|---|
| SILstat design approval | Apr 25 | In progress |
| SIL Verification design approval | May 2 | In progress |
| SAEL Safety design approval | May 9 | Shaping |
| FSE 107 design approval | May 9 | Pending brief |
| SILstat development complete | May 16 | Pending |
| SIL Verification development complete | May 23 | Pending |
| SAEL Safety development complete | May 30 | Pending |
| FSE 107 development complete | May 30 | Pending |
| Full QA pass | Jun 6 | Pending |
| Client sign-off | Jun 13 | Pending |
| Launch | Jun 20 | Pending |

### Risk Factors

**SAEL Safety copy delay** � If Todd doesn't have final product copy by May 2, the SAEL Safety page dev timeline slips by one week.

**Photography for SAEL** � If product photography is not available, we need to commission a 3D render, which adds 1�2 weeks of lead time.

**Wix Studio limitations** � The interactive diagram on SIL Verification may not be feasible in Wix Studio without custom code. Mitigation: scope it as static first.

---

## Budget & Scope

### Approved Scope

The approved budget covers:
- Design for all four pages
- Development for all four pages
- One round of revisions per page post-client review
- QA and launch support

### Out of Scope

The following items are explicitly out of scope for this project:

- Site-wide navigation changes
- Backend integrations (forms use Wix native forms only)
- SEO audit or restructuring
- Blog or content management changes
- Maintenance beyond 30 days post-launch

### Change Order Triggers

Any of the following would require a change order:
- Additional pages beyond the four defined
- Additional revision rounds beyond one per page
- Custom interactive components beyond the static SVG diagram
- Third-party integrations (HubSpot, Salesforce, etc.)

---

## Communication

### Basecamp Thread

All project communication happens in the exida Basecamp project. The primary thread is "Upcoming Work � April 6 Review Meeting" which has evolved into the general project planning thread.

### Meeting Cadence

- **Weekly check-in:** Thursdays at 10am PT with Todd
- **Design reviews:** As needed, scheduled via Basecamp message
- **QA review:** One dedicated session per page before client sign-off

### Key Contacts

| Name | Role | Notes |
|---|---|---|
| Todd | Client PM | Primary contact, copy owner |
| Rowan | Hop PM + Dev | Project lead |
| Alice | Design | All four pages |

---

## Appendix

### Wix Studio Notes

The exida site runs on Wix Studio (not legacy Wix Editor). Key implications:
- CSS Grid is available for layout
- Custom components via Wix Blocks if needed
- Forms use Wix Forms (native), not third-party

### Reference Documents

- [SILstat Design Figma](#) � Link to current design file
- [SIL Verification Design Figma](#) � Link to current design file
- [SAEL Safety Brief](#) � Google Doc with product brief
- [FSE 107 Course Description](#) � Google Doc with course content

### Glossary

**SIL** � Safety Integrity Level. A measure of safety system performance required by IEC 61508/61511.

**SAEL** � Safety Application Engineering Language. exida's proprietary hardware product line.

**FSE** � Functional Safety Engineer. Professional certification track for which FSE 107 is a course module.

**Wix Studio** � Wix's professional web development platform, distinct from the legacy Wix Editor.
`

// ---------------------------------------------------------------------------
// Heading extraction utility
// ---------------------------------------------------------------------------

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
}

function extractHeadings(markdown: string): Omit<Heading, 'ratio'>[] {
  const lines = markdown.split('\n')
  const headings: Omit<Heading, 'ratio'>[] = []
  for (const line of lines) {
    const match = line.match(/^(#{1,3})\s+(.+)$/)
    if (match) {
      const level = match[1].length as 1 | 2 | 3
      const text = match[2].trim()
      headings.push({ level, text, id: slugify(text) })
    }
  }
  return headings
}

// ---------------------------------------------------------------------------
// Render markdown to HTML (very lightweight)
// ---------------------------------------------------------------------------

function renderMarkdown(md: string): string {
  const lines = md.split('\n')
  const html: string[] = []
  let inCode = false
  let inTable = false
  let inList = false

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    if (line.startsWith('```')) {
      if (inList) { html.push('</ul>'); inList = false }
      if (inTable) { html.push('</tbody></table>'); inTable = false }
      inCode = !inCode
      if (inCode) html.push('<pre class="code-block"><code>')
      else html.push('</code></pre>')
      continue
    }

    if (inCode) {
      html.push(escapeHtml(line))
      continue
    }

    // Tables
    if (line.startsWith('|')) {
      if (inList) { html.push('</ul>'); inList = false }
      if (!inTable) {
        inTable = true
        html.push('<table class="md-table"><thead>')
        const cells = line.split('|').filter(Boolean).map(c => c.trim())
        html.push('<tr>' + cells.map(c => `<th>${c}</th>`).join('') + '</tr>')
        html.push('</thead><tbody>')
        i++ // skip separator row
      } else {
        const cells = line.split('|').filter(Boolean).map(c => c.trim())
        html.push('<tr>' + cells.map(c => `<td>${c}</td>`).join('') + '</tr>')
      }
      continue
    } else if (inTable) {
      html.push('</tbody></table>')
      inTable = false
    }

    // Headings
    const hMatch = line.match(/^(#{1,3})\s+(.+)$/)
    if (hMatch) {
      if (inList) { html.push('</ul>'); inList = false }
      const level = hMatch[1].length
      const text = hMatch[2].trim()
      const id = slugify(text)
      html.push(`<h${level} id="${id}" class="md-h${level}">${text}</h${level}>`)
      continue
    }

    // HR
    if (line.match(/^---+$/)) {
      if (inList) { html.push('</ul>'); inList = false }
      html.push('<hr class="md-hr" />')
      continue
    }

    // List items
    if (line.match(/^[-*]\s/)) {
      if (!inList) { html.push('<ul class="md-ul">'); inList = true }
      const content = inlineMarkdown(line.replace(/^[-*]\s/, ''))
      html.push(`<li class="md-li">${content}</li>`)
      continue
    } else if (inList && line.trim() === '') {
      html.push('</ul>')
      inList = false
    }

    // Checkbox list
    if (line.match(/^- \[[ x]\]/)) {
      if (inList) { html.push('</ul>'); inList = false }
      const checked = line.includes('- [x]')
      const content = inlineMarkdown(line.replace(/^- \[[ x]\]\s*/, ''))
      html.push(`<div class="md-checkbox"><input type="checkbox" ${checked ? 'checked' : ''} disabled /><span>${content}</span></div>`)
      continue
    }

    // Blank line
    if (line.trim() === '') {
      if (inList) { html.push('</ul>'); inList = false }
      html.push('<div class="md-spacer"></div>')
      continue
    }

    // Paragraph
    if (!inList) {
      html.push(`<p class="md-p">${inlineMarkdown(line)}</p>`)
    }
  }

  if (inList) html.push('</ul>')
  if (inTable) html.push('</tbody></table>')

  return html.join('\n')
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function inlineMarkdown(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code class="md-code">$1</code>')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '<a class="md-link">$1</a>')
}

// ---------------------------------------------------------------------------
// Overview Ruler component
// Floats as an absolute overlay on the right edge of the content area.
// Hidden by default; parent controls visibility via CSS hover.
// ---------------------------------------------------------------------------

interface OverviewRulerProps {
  scrollEl: HTMLElement | null
}

function OverviewRuler({ scrollEl }: OverviewRulerProps) {
  const rulerRef = useRef<HTMLDivElement>(null)
  const vpRef = useRef<HTMLDivElement>(null)
  const scrollStateRef = useRef({ scrollTop: 0, scrollHeight: 1, clientHeight: 1 })
  const draggingRef = useRef(false)
  const dragOffsetRef = useRef(0)

  // DOM-direct scroll sync -- no setState means no React re-renders on scroll
  useEffect(() => {
    if (!scrollEl) return
    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollEl
      scrollStateRef.current = { scrollTop, scrollHeight, clientHeight }
      const scrollable = scrollHeight - clientHeight
      const vpTop = scrollable > 0 ? scrollTop / scrollHeight : 0
      const vpHeight = clientHeight / scrollHeight
      if (vpRef.current) {
        vpRef.current.style.top = `${vpTop * 100}%`
        vpRef.current.style.height = `${Math.max(vpHeight * 100, 2)}%`
      }
    }
    update()
    scrollEl.addEventListener('scroll', update, { passive: true })
    const ro = new ResizeObserver(update)
    ro.observe(scrollEl)
    return () => {
      scrollEl.removeEventListener('scroll', update)
      ro.disconnect()
    }
  }, [scrollEl])

  // Drag/click handler attached imperatively -- avoids stale closure issues
  useEffect(() => {
    const ruler = rulerRef.current
    if (!ruler || !scrollEl) return

    const onMouseDown = (e: MouseEvent) => {
      e.preventDefault()
      const rect = ruler.getBoundingClientRect()
      const { scrollHeight, clientHeight } = scrollStateRef.current
      const scrollable = scrollHeight - clientHeight
      const vpTop = scrollable > 0 ? scrollEl.scrollTop / scrollHeight : 0
      const vpHeight = clientHeight / scrollHeight
      const clickRatio = (e.clientY - rect.top) / rect.height
      const isOnViewport = clickRatio >= vpTop && clickRatio <= vpTop + vpHeight

      draggingRef.current = true
      if (isOnViewport) {
        dragOffsetRef.current = clickRatio - vpTop
      } else {
        const targetRatio = Math.max(0, Math.min(1 - vpHeight, clickRatio - vpHeight / 2))
        scrollEl.scrollTop = targetRatio * scrollHeight
        dragOffsetRef.current = vpHeight / 2
      }

      const onMove = (e: MouseEvent) => {
        if (!draggingRef.current) return
        const { scrollHeight, clientHeight } = scrollStateRef.current
        const vpHeight = clientHeight / scrollHeight
        const rect = ruler.getBoundingClientRect()
        const clickRatio = (e.clientY - rect.top) / rect.height
        scrollEl.scrollTop = Math.max(0, Math.min(1 - vpHeight, clickRatio - dragOffsetRef.current)) * scrollHeight
      }
      const onUp = () => {
        draggingRef.current = false
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    }

    ruler.addEventListener('mousedown', onMouseDown)
    return () => ruler.removeEventListener('mousedown', onMouseDown)
  }, [scrollEl])

  return (
    <div ref={rulerRef} className="overview-ruler">
      <div ref={vpRef} className="ruler-viewport" />
    </div>
  )
}

// ---------------------------------------------------------------------------
// TOC Panel component
// ---------------------------------------------------------------------------

interface TocPanelProps {
  headings: Heading[]
  activeId: string | null
  onHeadingClick: (id: string) => void
  onClose: () => void
  pinned: boolean
  onPinToggle: () => void
}

function TocPanel({ headings, activeId, onHeadingClick, onClose, pinned, onPinToggle }: TocPanelProps) {
  const sizeMap: Record<1 | 2 | 3, string> = {
    1: 'text-xs font-semibold',
    2: 'text-xs font-medium',
    3: 'text-xs font-normal',
  }

  return (
    <div className="toc-panel">
      {/* Header */}
      <div className="toc-header">
        <span className="toc-title">Contents</span>
        <div className="toc-header-actions">
          <button
            className={cn('toc-icon-btn', pinned && 'toc-icon-btn--active')}
            onClick={onPinToggle}
            title={pinned ? 'Unpin (auto-close on click)' : 'Pin open'}
          >
            <Pin className="w-3.5 h-3.5" />
          </button>
          <button className="toc-icon-btn" onClick={onClose} title="Close">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Heading list */}
      <div className="toc-list">
        {headings.map((h) => (
          <button
            key={h.id}
            data-level={h.level}
            className={cn(
              'toc-item',
              sizeMap[h.level],
              activeId === h.id && 'toc-item--active'
            )}
            onClick={() => onHeadingClick(h.id)}
          >
            {activeId === h.id && (
              <ChevronRight className="toc-item-arrow" />
            )}
            <span className="toc-item-text">{h.text}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Current Section Pill component
// ---------------------------------------------------------------------------

interface CurrentSectionPillProps {
  activeHeading: Heading | null
  onClick: () => void
  tocOpen: boolean
}

function CurrentSectionPill({ activeHeading, onClick, tocOpen }: CurrentSectionPillProps) {
  if (!activeHeading) return null

  return (
    <button
      className={cn('section-pill', tocOpen && 'section-pill--active')}
      onClick={onClick}
      title="Open table of contents"
    >
      <List className="w-3.5 h-3.5 section-pill-icon" />
      <span className="section-pill-text">{activeHeading.text}</span>
      <ChevronRight className={cn('w-3 h-3 section-pill-chevron', tocOpen && 'rotate-90')} />
    </button>
  )
}

// ---------------------------------------------------------------------------
// Main prototype component
// ---------------------------------------------------------------------------

export function DocTocRulerTest() {
  const contentRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  const [tocOpen, setTocOpen] = useState(false)
  const [pinned, setPinned] = useState(false)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [headings, setHeadings] = useState<Heading[]>([])

  const rawHeadings = extractHeadings(MOCK_CONTENT)
  const renderedHtml = React.useMemo(() => renderMarkdown(MOCK_CONTENT), [])

  // Compute heading DOM positions once content is rendered
  useEffect(() => {
    const scrollEl = scrollRef.current
    const contentEl = contentRef.current
    if (!scrollEl || !contentEl) return

    const updateRatios = () => {
      const totalHeight = scrollEl.scrollHeight
      const updated = rawHeadings.map((h) => {
        const el = contentEl.querySelector(`#${h.id}`) as HTMLElement | null
        const ratio = el ? el.offsetTop / totalHeight : 0
        return { ...h, ratio }
      })
      setHeadings(updated)
    }

    updateRatios()
    const ro = new ResizeObserver(updateRatios)
    ro.observe(contentEl)
    return () => ro.disconnect()
  }, [renderedHtml])

  // Track active heading via IntersectionObserver
  useEffect(() => {
    const contentEl = contentRef.current
    if (!contentEl || headings.length === 0) return

    const headingEls = headings
      .map((h) => contentEl.querySelector(`#${h.id}`) as HTMLElement | null)
      .filter(Boolean) as HTMLElement[]

    if (headingEls.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the topmost heading that is intersecting or has passed the viewport top
        const scrollEl = scrollRef.current
        if (!scrollEl) return

        const scrollTop = scrollEl.scrollTop
        const totalHeight = scrollEl.scrollHeight

        // Walk headings from bottom to top; the last one whose offsetTop <= scrollTop + 80px is active
        let current: string | null = headings[0]?.id ?? null
        for (const h of headings) {
          const el = contentEl.querySelector(`#${h.id}`) as HTMLElement | null
          if (el && el.offsetTop <= scrollTop + 100) {
            current = h.id
          }
        }
        setActiveId(current)
      },
      {
        root: scrollRef.current,
        rootMargin: '-20px 0px -80% 0px',
        threshold: 0,
      }
    )

    headingEls.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [headings])

  // Also update active heading on scroll (more reliable than IO alone)
  useEffect(() => {
    const scrollEl = scrollRef.current
    const contentEl = contentRef.current
    if (!scrollEl || !contentEl || headings.length === 0) return

    const onScroll = () => {
      const scrollTop = scrollEl.scrollTop
      let current: string | null = headings[0]?.id ?? null
      for (const h of headings) {
        const el = contentEl.querySelector(`#${h.id}`) as HTMLElement | null
        if (el && el.offsetTop <= scrollTop + 100) {
          current = h.id
        }
      }
      setActiveId(current)
    }

    scrollEl.addEventListener('scroll', onScroll, { passive: true })
    return () => scrollEl.removeEventListener('scroll', onScroll)
  }, [headings])

  const scrollToHeading = (id: string) => {
    const contentEl = contentRef.current
    const scrollEl = scrollRef.current
    if (!contentEl || !scrollEl) return
    const el = contentEl.querySelector(`#${id}`) as HTMLElement | null
    if (el) {
      scrollEl.scrollTo({ top: el.offsetTop - 16, behavior: 'smooth' })
      setActiveId(id)
      if (!pinned) {
        setTimeout(() => setTocOpen(false), 300)
      }
    }
  }

  const activeHeading = headings.find((h) => h.id === activeId) ?? null

  return (
    <>
      {/* Inline styles */}
      <style>{`
        .doc-toc-prototype {
          display: flex;
          flex-direction: column;
          height: 100vh;
          background: var(--background);
          color: var(--foreground);
          font-family: inherit;
        }

        /* ?? Toolbar ?? */
        .proto-toolbar {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 16px;
          border-bottom: 1px solid var(--border);
          background: var(--sidebar);
          flex-shrink: 0;
          z-index: 10;
        }
        .proto-toolbar-title {
          font-size: 14px;
          font-weight: 600;
          flex: 1;
          opacity: 0.9;
        }
        .proto-toolbar-badge {
          font-size: 11px;
          padding: 2px 8px;
          border-radius: 999px;
          background: hsl(var(--primary) / 0.12);
          color: hsl(var(--primary));
          border: 1px solid hsl(var(--primary) / 0.2);
        }
        .proto-toc-toggle {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 5px 10px;
          border-radius: 6px;
          border: 1px solid var(--border);
          background: transparent;
          color: var(--muted-foreground);
          font-size: 12px;
          cursor: pointer;
          transition: all 0.15s;
        }
        .proto-toc-toggle:hover {
          background: var(--accent);
          color: var(--foreground);
          border-color: var(--border);
        }
        .proto-toc-toggle--active {
          background: hsl(var(--primary) / 0.1);
          color: hsl(var(--primary));
          border-color: hsl(var(--primary) / 0.3);
        }

        /* ?? Body layout ?? */
        .proto-body {
          display: flex;
          flex: 1;
          overflow: hidden;
          position: relative;
        }

        /* ?? TOC Panel ?? */
        .toc-panel {
          width: 200px;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          border-right: 1px solid var(--border);
          background: var(--sidebar);
          overflow: hidden;
          animation: tocSlideIn 0.18s ease-out;
        }
        @keyframes tocSlideIn {
          from { transform: translateX(-100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .toc-header {
          display: flex;
          align-items: center;
          padding: 10px 12px 8px;
          border-bottom: 1px solid var(--border);
          flex-shrink: 0;
        }
        .toc-title {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: var(--muted-foreground);
          flex: 1;
        }
        .toc-header-actions {
          display: flex;
          gap: 2px;
        }
        .toc-icon-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 4px;
          border: none;
          background: transparent;
          color: var(--muted-foreground);
          cursor: pointer;
          transition: all 0.12s;
        }
        .toc-icon-btn:hover {
          background: var(--accent);
          color: var(--foreground);
        }
        .toc-icon-btn--active {
          color: hsl(var(--primary));
          background: hsl(var(--primary) / 0.1);
        }
        .toc-list {
          flex: 1;
          overflow-y: auto;
          padding: 6px 0;
        }
        .toc-item {
          display: flex;
          align-items: center;
          width: 100%;
          padding: 3px 12px 3px 12px;
          border: none;
          background: transparent;
          color: var(--muted-foreground);
          text-align: left;
          cursor: pointer;
          border-radius: 0;
          transition: all 0.1s;
          position: relative;
          gap: 4px;
          line-height: 1.5;
        }
        .toc-item[data-level="2"] { padding-left: 22px; }
        .toc-item[data-level="3"] { padding-left: 34px; color: hsl(var(--muted-foreground) / 0.75); }
        .toc-item:hover {
          background: var(--accent);
          color: var(--foreground);
        }
        .toc-item--active {
          color: hsl(var(--primary));
          background: hsl(var(--primary) / 0.08);
        }
        .toc-item-arrow {
          width: 10px;
          height: 10px;
          flex-shrink: 0;
          opacity: 0.8;
        }
        .toc-item-text {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        /* ?? Content area ?? */
        .proto-content-area {
          flex: 1;
          display: flex;
          overflow: hidden;
          position: relative;
        }

        /* ?? Scroll container ?? */
        .proto-scroll {
          flex: 1;
          overflow-y: scroll;
          overflow-x: hidden;
          position: relative;
          scrollbar-width: none;
        }
        .proto-scroll::-webkit-scrollbar {
          display: none;
        }

        /* ?? Current section pill ?? */
        .section-pill {
          position: sticky;
          top: 0;
          z-index: 5;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin: 10px 24px;
          padding: 5px 10px 5px 8px;
          border-radius: 20px;
          border: 1px solid var(--border);
          background: var(--background);
          color: var(--muted-foreground);
          font-size: 12px;
          cursor: pointer;
          transition: all 0.15s;
          box-shadow: 0 1px 4px hsl(0 0% 0% / 0.08);
          max-width: calc(100% - 48px);
          text-align: left;
        }
        .section-pill:hover {
          background: var(--accent);
          color: var(--foreground);
          border-color: hsl(var(--primary) / 0.3);
        }
        .section-pill--active {
          background: hsl(var(--primary) / 0.08);
          border-color: hsl(var(--primary) / 0.3);
          color: hsl(var(--primary));
        }
        .section-pill-icon {
          flex-shrink: 0;
          opacity: 0.7;
        }
        .section-pill-text {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-weight: 500;
        }
        .section-pill-chevron {
          flex-shrink: 0;
          opacity: 0.5;
          transition: transform 0.15s;
        }

        /* ?? Overview Ruler � floats over right edge, appears on hover ?? */
        .overview-ruler {
          position: absolute;
          right: 0;
          top: 0;
          bottom: 0;
          width: 12px;
          opacity: 0;
          transition: opacity 0.18s ease;
          cursor: pointer;
          user-select: none;
          z-index: 20;
        }
        .proto-content-area:hover .overview-ruler {
          opacity: 1;
        }
        .ruler-viewport {
          position: absolute;
          left: 1px;
          right: 1px;
          background: hsl(var(--primary) / 0.15);
          border: 1px solid hsl(var(--primary) / 0.25);
          border-top: 2px solid hsl(var(--primary) / 0.85);
          border-radius: 2px;
          pointer-events: none;
          min-height: 4px;
          transition: top 0.05s, height 0.05s;
        }

        /* ?? Rendered markdown ?? */
        .proto-doc {
          padding: 24px 40px 80px;
          max-width: 720px;
          margin: 0 auto;
        }
        .md-h1 {
          font-size: 22px;
          font-weight: 700;
          margin: 32px 0 12px;
          color: var(--foreground);
          line-height: 1.3;
        }
        .md-h1:first-child { margin-top: 8px; }
        .md-h2 {
          font-size: 16px;
          font-weight: 600;
          margin: 24px 0 8px;
          color: var(--foreground);
        }
        .md-h3 {
          font-size: 14px;
          font-weight: 600;
          margin: 16px 0 6px;
          color: var(--foreground);
          opacity: 0.85;
        }
        .md-p {
          font-size: 14px;
          line-height: 1.7;
          color: var(--muted-foreground);
          margin: 0 0 10px;
        }
        .md-ul {
          margin: 0 0 10px 20px;
          list-style: disc;
        }
        .md-li {
          font-size: 14px;
          line-height: 1.7;
          color: var(--muted-foreground);
          margin-bottom: 2px;
        }
        .md-hr {
          border: none;
          border-top: 1px solid var(--border);
          margin: 20px 0;
        }
        .md-spacer { height: 4px; }
        .md-code {
          font-family: monospace;
          font-size: 12px;
          background: var(--muted);
          padding: 1px 4px;
          border-radius: 3px;
        }
        .md-link { color: hsl(var(--primary)); text-decoration: underline; cursor: pointer; }
        .code-block {
          background: var(--muted);
          border-radius: 6px;
          padding: 12px 16px;
          font-size: 12px;
          font-family: monospace;
          overflow-x: auto;
          margin: 8px 0 12px;
          white-space: pre;
          line-height: 1.5;
        }
        .code-block code { background: none; padding: 0; }
        .md-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
          margin: 8px 0 14px;
        }
        .md-table th, .md-table td {
          border: 1px solid var(--border);
          padding: 6px 10px;
          text-align: left;
        }
        .md-table th {
          background: var(--muted);
          font-weight: 600;
          font-size: 12px;
        }
        .md-checkbox {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          margin-bottom: 4px;
          font-size: 14px;
          color: var(--muted-foreground);
          line-height: 1.5;
        }
        .md-checkbox input { margin-top: 3px; flex-shrink: 0; }
      `}</style>

      <div className="doc-toc-prototype">
        {/* Toolbar */}
        <div className="proto-toolbar">
          <span className="proto-toolbar-title">exida Web Project Plan</span>
          <span className="proto-toolbar-badge">Prototype</span>
          <button
            className={cn('proto-toc-toggle', tocOpen && 'proto-toc-toggle--active')}
            onClick={() => setTocOpen((v) => !v)}
            title="Toggle table of contents"
          >
            <List className="w-3.5 h-3.5" />
            TOC
          </button>
        </div>

        {/* Body */}
        <div className="proto-body">
          {/* TOC Panel (conditional) */}
          {tocOpen && (
            <TocPanel
              headings={headings}
              activeId={activeId}
              onHeadingClick={scrollToHeading}
              onClose={() => setTocOpen(false)}
              pinned={pinned}
              onPinToggle={() => setPinned((v) => !v)}
            />
          )}

          {/* Content area + floating ruler */}
          <div className="proto-content-area">
            <div ref={scrollRef} className="proto-scroll">
              {/* Current section pill (sticky) */}
              <CurrentSectionPill
                activeHeading={activeHeading}
                onClick={() => setTocOpen((v) => !v)}
                tocOpen={tocOpen}
              />

              {/* Document content */}
              <div
                ref={contentRef}
                className="proto-doc"
                dangerouslySetInnerHTML={{ __html: renderedHtml }}
              />
            </div>

            {/* Overview ruler � absolute, revealed on hover */}
            <OverviewRuler scrollEl={scrollRef.current} />
          </div>
        </div>
      </div>
    </>
  )
}
